import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';

type Props = {
  /** Крупный заголовок экрана: «Привет, Айда», «Словарь», «Расписание». */
  title: string;
  /** Строка под ним — пояснение или подсказка дня. */
  subtitle?: string;
  /** Значок слева от заголовка: обложка курса на экране урока. */
  icon?: ReactNode;
  /** Дней подряд. Полоска прячется на нуле: «0 дней подряд» — не достижение. */
  streak?: number;
  /** Непрочитанные уведомления — точкой на колокольчике. */
  unread?: number;
  name?: string;
  avatarUrl?: string;
};

/**
 * Шапка экранов ученика по макету «Desktop Web v2»: слева заголовок с
 * подписью, справа — дни подряд, уведомления и лицо.
 *
 * Живёт отдельным куском, а не внутри оболочки, потому что экраны переносим
 * на макет по одному: пока часть из них со старой шапкой, общая наверху
 * давала бы на них два заголовка подряд.
 *
 * ⚠️ Кнопки «Скрыть / Показать истории» здесь больше нет (сторис 2.0, 13.10.2026): кружки живут в ленивом куске
 * (`pages/student/StoryBarSlot.tsx`), а строки сторис для ученика в основной кусок не попадают — его получает и
 * бесплатник. В утверждённых макетах кружки не сворачиваются.
 */
export function StudentTopBar({ title, subtitle, icon, streak = 0, unread = 0, name, avatarUrl }: Props) {
  const { t } = useTranslation();

  return (
    <div className="mb-4 flex items-start justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3.5">
        {icon}
        <div className="min-w-0">
          <h1 className="font-display text-[26px] font-bold leading-[1.12] tracking-display text-on-surface">{title}</h1>
          {subtitle && <p className="mt-[5px] truncate text-[13.5px] text-on-surface-variant">{subtitle}</p>}
        </div>
      </div>

      <div className="flex flex-none items-center gap-2.5">
        {streak > 0 && (
          <div className="flex items-center gap-[7px] rounded-full bg-primary-container px-3.5 py-2">
            <span className="text-[13px]">🔥</span>
            <span className="text-[13px] font-bold tabular-nums text-on-primary-container">{streak}</span>
            <span className="text-[11px] font-semibold text-on-primary-container/75">{t('dashboard.streakLabel')}</span>
          </div>
        )}

        <Link
          to="/app/notifications"
          aria-label={t('dashboard.unreadNotifications', { count: unread })}
          className="relative grid h-10 w-10 place-items-center rounded-full bg-surface-container text-on-surface transition hover:bg-surface-container-high"
        >
          <span className="material-symbols-outlined text-[19px]">notifications</span>
          {unread > 0 && (
            <span className="absolute -right-px -top-px h-[9px] w-[9px] rounded-full bg-primary" />
          )}
        </Link>

        <Link to="/app/profile" aria-label={t('nav.profile')}>
          {/* Кружок с буквами — синий, как в макете; фотография, если она есть. */}
          <Avatar name={name} src={avatarUrl} size="md" className={cn(!avatarUrl && 'bg-info-container text-[12.5px] font-bold text-info')} />
        </Link>
      </div>
    </div>
  );
}
